/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getAmc, getAmcByCode } from "../stores/services/amc.service";
import { sortAMCS, typeRatioSystematic } from "../utils/customFunction";
import moment from "moment";
import {
  addInvestmentTransaction,
  getInvestmentTransactionById,
  getModeOfPayments,
  updateVPSTxn,
} from "../stores/services/transactions.service";
import {
  getDailyNavByDate,
  getFundByAmc,
  getInstrumentType,
} from "../stores/services/funds.service";
import { getAccountByFund } from "../stores/services/account.service";
import {
  getSearchUnitholders,
  getUnitHoldersByAmcAndFolioNo,
} from "../stores/services/unit-holder.service";
import useCustomStyles from "./useCustomStyles";
import { Label } from "reactstrap";
import {
  defaultFields,
  InvestmentTransactionFields,
} from "./constants/vps-constants";

export interface CoaFundItem {
  id: string;
  from_fund: string;
  from_fund_unit: number;
  from_fund_nav: number;
  from_fund_acc: string;
  from_fund_acc_title: string;
  backend_load: number;
  pct: number;
  amount: number;
  to_fund: string;
  to_fund_acc: string;
  to_fund_acc_title: string;
  to_fund_unit: number;
  to_fund_nav: number;
  frontend_load: number;
  // UI helpers
  from_accounts: any[];
  to_accounts: any[];
  selected_from_account: any;
  selected_to_account: any;
  to_fund_options: any[];
}

export const useInvestmentTransaction = () => {
  const history = useHistory();
  const email = sessionStorage.getItem("email") || "";
  const role = sessionStorage.getItem("role") || "";
  const [totalAmount, setTotalAmount] = useState<any>();
  const [totalUnits, setTotalUnits] = useState<any>();
  const [units, setUnits] = useState<any>();
  const [amcOptions, setAmcOptions] = useState<any[]>([]);
  const [fundOptions, setFundOptions] = useState<any[]>([]);
  const [accountOptions, setAccountOptions] = useState<any[]>([]);
  const [offerPrice, setOfferPrice] = useState<any>();
  const [unitholderAccountOptions, setUnitholderAccountOptions] = useState<
    any[]
  >([]);
  const [unitholderOptions, setUnitholderOptions] = useState<any[]>([]);
  const [unitholderLoading, setUnitholderLoading] = useState(false);
  const [unitholderSearchInput, setUnitholderSearchInput] = useState("");
  const [noNav, setNoNav] = useState(false);

  const [modeOfPayments, setModeOfPayments] = useState<string[]>([]);
  const [instrumentTypes, setInstrumentTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorShow, setErrorShow] = useState(false);

  const today = moment().format("YYYY-MM-DD");
  const [allFunds, setAllFunds] = useState<any[]>([]);
  const [errors, setErrors] = useState<{
    [key: number | string]: string | null;
  }>({});
  const [rangeTimer, setRangeTimer] = useState<any>(null);
  const [autoInvest, setAutoInvest] = useState("false");
  const [typeVPS, setTypeVPS] = useState("");
  const [unitholderType, setUnitholderType] = useState("");
  const [amount, setAmount] = useState<any>();
  const [currAmount, setCurrAmount] = useState<any>();
  const [fundLoading, setFundLoading] = useState<any>();
  const [navLoading, setNavLoading] = useState<any>();
  const [subFundsBlocks, setSubFundsBlocks] = useState<any>([]);
  const [currentSubFundsBlock, SetCurrentSubFundsBlock] = useState<any>();
  const { customStyles } = useCustomStyles();
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const [totalInvestment, setTotalInvesment] = useState(0);
  const [showApproval, setShowApproval] = useState(false);
  const [authroizeDialogue, setAuthorizeDialogue] = useState(false);
  const [decision, setDecision] = useState(""); // "" | "accept" | "reject"
  const [rejectReason, setRejectReason] = useState("");
  const [compliant, setCompliant] = useState("");
  const [compliantComment, setcompliantComment] = useState("");
  const isManualUpdate = useRef(false);

  const [showVpsErrors, setShowVpsErrors] = useState(false);
  const [vpsErrorMessages, setVpsErrorMessages] = useState([]);
  const [viewMode, setViewMode] = useState<
    "investment" | "withdrawal" | "changeofallocation" | "view"
  >("investment");
  const [loadingViewData, setTxnLoading] = useState<boolean>(false);

  // New state for fund accounts
  const [fundAccountsMap, setFundAccountsMap] = useState<{
    [key: string]: any[];
  }>({});

  const [parentFundAccounts, setParentFundAccounts] = useState<any>([]);
  const [parentFundAccount, setParentFundAccount] = useState<any>({});

  const [fields, setFields] = useState<InvestmentTransactionFields>(
    defaultFields(today),
  );

  // Add these to your state in the hook
  const [coaFunds, setCoaFunds] = useState<CoaFundItem[]>([]);
  const [fromScheme, setFromScheme] = useState("");
  const [toScheme, setToScheme] = useState("");
  const [availableSchemes] = useState([
    "Balanced",
    "Aggressive",
    "Conservative",
  ]);

  // Add this computation for scheme options
  const schemeOptions = useMemo(() => {
    return availableSchemes
      .filter((s) => s !== fromScheme)
      .map((s) => ({ value: s, label: s }));
  }, [availableSchemes, fromScheme]);

  // Initialize CoA when unitholder changes
  const initializeCoA = async (systematicInvestment: any) => {
    if (viewMode !== "changeofallocation") return;

    const blocks = systematicInvestment?.investment_blocks || [];
    const currentScheme = systematicInvestment?.allocation_scheme || "";
    setFromScheme(currentScheme);

    const newFunds: CoaFundItem[] = [];

    for (const block of blocks) {
      for (const subFund of block.subFunds || []) {
        const fundCode = subFund.fund_code;
        const navData: any = await fetchNavForFund(fundCode, fields.sale_date);
        const redemptionNav = navData?.redemption_price || 0;

        // Fetch accounts for this fund
        const accounts = await fetchAccountsForFund(fundCode);
        const collectionAcc = findCollectionAccount(accounts);

        newFunds.push({
          id: `${block.id}_${fundCode}_${Date.now()}`,
          from_fund: fundCode,
          from_fund_unit: 0,
          from_fund_nav: redemptionNav,
          from_fund_acc: collectionAcc?.account_no || "",
          from_fund_acc_title: collectionAcc?.account_title || "",
          backend_load: parseFloat(subFund.backend_load || 0),
          pct: 0,
          amount: 0,
          to_fund: "",
          to_fund_acc: "",
          to_fund_acc_title: "",
          to_fund_unit: 0,
          to_fund_nav: 0,
          frontend_load: 0,
          from_accounts: accounts,
          to_accounts: [],
          selected_from_account: collectionAcc,
          selected_to_account: null,
          to_fund_options: [],
        });
      }
    }

    setCoaFunds(newFunds);
  };

  // Handle from scheme change
  const handleFromSchemeChange = (scheme: string) => {
    setFromScheme(scheme);
    setToScheme(""); // Reset to scheme
  };

  // Handle to scheme change
  const handleToSchemeChange = async (scheme: string) => {
    setToScheme(scheme);
  };

  // Handle to fund selection
  const handleToFundChange = async (fundId: string, itemId: string) => {
    const item = coaFunds.find((f) => f.id === itemId);
    if (!item) return;

    const navData: any = await fetchNavForFund(fundId, fields.sale_date);
    const offerNav = navData?.offer_price || 0;

    // Fetch accounts for target fund
    const accounts = await fetchAccountsForFund(fundId);
    const collectionAcc = findCollectionAccount(accounts);

    setCoaFunds((prev) =>
      prev.map((fund) => {
        if (fund.id !== itemId) return fund;

        return {
          ...fund,
          to_fund: fundId,
          to_fund_nav: offerNav,
          to_accounts: accounts,
          selected_to_account: collectionAcc,
          to_fund_acc: collectionAcc?.account_no || "",
          to_fund_acc_title: collectionAcc?.account_title || "",
          frontend_load: parseFloat(collectionAcc?.frontend_load || 0),
        };
      }),
    );

    // Recalculate if units exist
    if (item.from_fund_unit > 0) {
      recalculateCoaItem(itemId);
    }
  };

  // Handle from fund unit change (Case 1: Move units only)
  const handleFromUnitChange = (itemId: string, units: number) => {
    const item = coaFunds.find((f) => f.id === itemId);
    if (!item) return;

    // Validation: Cannot exceed holding
    const holding = parseFloat(
      currentSubFundsBlock
        ?.flatMap((b) => b.subFunds)
        ?.find((s) => s.fund_code === item.from_fund)?.holding || 0,
    );

    if (units > holding) {
      setIndexedError(
        `coa_${itemId}_units`,
        `Cannot exceed holding: ${holding}`,
      );
      return;
    } else {
      setIndexedError(`coa_${itemId}_units`, null);
    }

    // Calculate amount from units * from_nav
    const amount = units * item.from_fund_nav;

    // Calculate to_fund_unit if to_fund selected
    let toUnits = 0;
    let toNav = item.to_fund_nav;

    if (item.to_fund && toNav > 0) {
      toUnits = amount / toNav;
    }

    setCoaFunds((prev) =>
      prev.map((fund) => {
        if (fund.id !== itemId) return fund;
        return {
          ...fund,
          from_fund_unit: units,
          amount: amount,
          to_fund_unit: toUnits,
        };
      }),
    );
  };

  const handleFromPctChange = (itemId: string, pct: number) => {
    const item = coaFunds.find((f) => f.id === itemId);
    if (!item) return;

    // Validation: Cannot exceed holding
    const allocation = parseFloat(
      currentSubFundsBlock
        ?.flatMap((b) => b.subFunds)
        ?.find((s) => s.fund_code === item.from_fund)?.proportion || 0,
    );

    if (pct > allocation) {
      setIndexedError(
        `coa_${itemId}_pct`,
        `Cannot exceed allocation: ${allocation}`,
      );
      return;
    } else {
      setIndexedError(`coa_${itemId}_pct`, null);
    }

    setCoaFunds((prev) =>
      prev.map((fund) => {
        if (fund.id !== itemId) return fund;
        return {
          ...fund,
          pct: pct,
        };
      }),
    );
  };

  // Handle amount change (Case 2: Move allocation %)
  const handleCoaAmountChange = (itemId: string, amount: number) => {
    const item = coaFunds.find((f) => f.id === itemId);
    if (!item) return;

    // Validation: Cannot exceed holding value
    const holding = parseFloat(
      currentSubFundsBlock
        ?.flatMap((b) => b.subFunds)
        ?.find((s) => s.fund_code === item.from_fund)?.holding || 0,
    );
    const maxAmount = holding * item.from_fund_nav;

    if (amount > maxAmount) {
      setIndexedError(
        `coa_${itemId}_amount`,
        `Max amount: ${maxAmount.toFixed(2)}`,
      );
      return;
    } else {
      setIndexedError(`coa_${itemId}_amount`, null);
    }

    // Calculate from units
    const fromUnits = amount / item.from_fund_nav;

    // Calculate to units
    let toUnits = 0;
    if (item.to_fund && item.to_fund_nav > 0) {
      toUnits = amount / item.to_fund_nav;
    }

    setCoaFunds((prev) =>
      prev.map((fund) => {
        if (fund.id !== itemId) return fund;
        return {
          ...fund,
          amount: amount,
          from_fund_unit: fromUnits,
          to_fund_unit: toUnits,
        };
      }),
    );
  };

  // Recalculate item when NAV changes
  const recalculateCoaItem = (itemId: string) => {
    const item = coaFunds.find((f) => f.id === itemId);
    if (!item) return;

    if (item.from_fund_unit > 0) {
      handleFromUnitChange(itemId, item.from_fund_unit);
    } else if (item.amount > 0) {
      handleCoaAmountChange(itemId, item.amount);
    }
  };

  // Add CoA fund row
  // Replace addCoaFundRow
  const addCoaFundRow = () => {
    const newItem: CoaFundItem = {
      id: `coa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      from_fund: "",
      from_fund_unit: 0,
      from_fund_nav: 0,
      from_fund_acc: "",
      from_fund_acc_title: "",
      backend_load: 0,
      pct: 0,
      amount: 0,
      to_fund: "",
      to_fund_acc: "",
      to_fund_acc_title: "",
      to_fund_unit: 0,
      to_fund_nav: 0,
      frontend_load: 0,
      from_accounts: [],
      to_accounts: [],
      selected_from_account: null,
      selected_to_account: null,
      to_fund_options: [],
    };
    setCoaFunds((prev) => [...prev, newItem]);
  };

  // Add new handler for from fund selection
  const handleFromFundChange = async (fundCode: string, itemId: string) => {
    if (!fundCode) return;

    try {
      const navData: any = await fetchNavForFund(fundCode, fields.sale_date);
      const redemptionNav = navData?.redemption_price || 0;
      const accounts = await fetchAccountsForFund(fundCode);
      const collectionAcc = findCollectionAccount(accounts);

      // Find the sub-fund details from currentSubFundsBlock for backend_load
      const subFundDetail = currentSubFundsBlock
        ?.flatMap((b: any) => b.subFunds || [])
        ?.find((s: any) => s.fund_code === fundCode);

      setCoaFunds((prev) =>
        prev.map((f) => {
          if (f.id !== itemId) return f;
          return {
            ...f,
            from_fund: fundCode,
            from_fund_nav: redemptionNav,
            from_fund_acc: collectionAcc?.account_no || "",
            from_fund_acc_title: collectionAcc?.account_title || "",
            backend_load: parseFloat(subFundDetail?.backend_load || 0),
            from_accounts: accounts,
            selected_from_account: collectionAcc,
            // Reset dependent fields when from fund changes
            from_fund_unit: 0,
            amount: 0,
            to_fund_unit: 0,
          };
        }),
      );
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || `Failed to load fund ${fundCode}`,
      );
    }
  };

  // Remove CoA fund row
  const removeCoaFundRow = (itemId: string) => {
    setCoaFunds((prev) => prev.filter((f) => f.id !== itemId));
    setIndexedError(`coa_${itemId}_units`, null);
    setIndexedError(`coa_${itemId}_amount`, null);
  };

  // Validate CoA before submit
  const validateCoA = (): boolean => {
    let valid = true;

    // toScheme is optional — blank means "within same scheme"
    if (coaFunds.length === 0) {
      toast.error("Please add at least one fund transfer");
      return false;
    }

    for (const fund of coaFunds) {
      if (!fund.from_fund) {
        setIndexedError(`coa_${fund.id}_fromfund`, "Please select source fund");
        valid = false;
      }
      if (!fund.to_fund) {
        setIndexedError(`coa_${fund.id}_tofund`, "Please select target fund");
        valid = false;
      }
      if (fund.from_fund && fund.to_fund && fund.from_fund === fund.to_fund) {
        setIndexedError(
          `coa_${fund.id}_tofund`,
          "Source and target fund must differ",
        );
        valid = false;
      }

      if (!fund.selected_from_account) {
        setIndexedError(`coa_${fund.id}_fromacc`, "Select source account");
        valid = false;
      }
      if (!fund.selected_to_account) {
        setIndexedError(`coa_${fund.id}_toacc`, "Select target account");
        valid = false;
      }
    }

    return valid;
  };

  // Build CoA payload for API
  const buildCoaPayload = () => {
    const effectiveToScheme = toScheme || fromScheme; // same scheme if not changed
    return {
      type: "changeofallocation",
      amc: fields.amc_code,
      folio: fields.folio_no,
      nav_date: fields.sale_date,
      realization_date: fields.settlement_date,
      email: email,
      source_txn_id: fields.txn_id || `COA-${fields.amc_code}-${Date.now()}`,
      from_scheme: fromScheme,
      to_scheme: effectiveToScheme,
      detail: toScheme
        ? `Change of Allocation from ${fromScheme} to ${toScheme}`
        : `Reallocation within ${fromScheme}`,
      funds: coaFunds.map((f) => ({
        from_fund: f.from_fund,
        from_fund_unit: parseFloat(f.from_fund_unit.toFixed(4)),
        from_fund_nav: parseFloat(f.from_fund_nav.toFixed(4)),
        from_fund_acc: f.from_fund_acc,
        backend_load: f.backend_load,
        pct: f.pct,
        amount: parseFloat(f.amount.toFixed(4)),
        to_fund: f.to_fund,
        to_fund_acc: f.to_fund_acc,
        to_fund_unit: parseFloat(f.to_fund_unit.toFixed(4)),
        to_fund_nav: parseFloat(f.to_fund_nav.toFixed(4)),
        frontend_load: f.frontend_load,
      })),
    };
  };

  useEffect(() => {
    if (location.pathname.includes("view-vps-transactions"))
      setViewMode("view");
    else if (location.pathname.includes("withdrawal"))
      setViewMode("withdrawal");
    else if (location.pathname.includes("investment"))
      setViewMode("investment");
    else if (location.pathname.includes("changeofallocation"))
      setViewMode("changeofallocation");
  }, []);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      if (viewMode === "view") return;
      try {
        const user = JSON.parse(sessionStorage.getItem("loggedInUser") || "{}");

        const [amcRes, modeRes, instrumentRes] = await Promise.all([
          getAmcByCode(email, user.amc_code),
          getModeOfPayments(email),
          getInstrumentType(email),
        ]);

        const selectedAmc = amcRes.data.data[0];
        handleAmcChange({
          ...selectedAmc,
          value: selectedAmc.amc_code,
          label: `${selectedAmc.name}`,
        });

        setModeOfPayments(modeRes.data.mode_of_payment);
        setInstrumentTypes(instrumentRes.data.instrument_types);
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || "Failed to load initial data",
        );
      }
    };
    fetchInitialData();
  }, [email, role]);

  const fetchInitialUnitholders = async (amcCode: string) => {
    try {
      setUnitholderLoading(true);
      const response = await getUnitHoldersByAmcAndFolioNo(
        email,
        amcCode,
        "",
        "1",
        "100",
        "Participant",
      );

      if (response.data.status === 200) {
        const options = response.data.data.map((uh: any) => {
          return {
            value: uh?.folio_no,
            label: `${uh?.folio_no} - ${uh?.name}`,
            unitholder_name: uh?.name,
            folio_no: uh?.folio_no,
            account_title: uh?.account_title,
            account_no: uh?.account_no,
            bank_name: uh?.bank_name,
            branch_name: uh?.branch_name,
            systematic_investment: uh?.systematic_investment || {},
          };
        });
        setUnitholderOptions(options);
      } else {
        setUnitholderOptions([]);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load unitholders");
      setUnitholderOptions([]);
    } finally {
      setUnitholderLoading(false);
    }
  };

  const searchUnitholders = async (searchValue: string, amcCode: string) => {
    if (!searchValue || searchValue.length < 2) {
      await fetchInitialUnitholders(amcCode);
      return;
    }

    try {
      setUnitholderLoading(true);
      const response = await getSearchUnitholders(
        email,
        amcCode,
        searchValue,
        "Participant",
      );

      if (response.data.status === 200) {
        // const options = response.data.data.map((uh: any) => ({
        const options = response.data.data?.map((uh: any) => ({
          value: uh?.folio_no,
          label: `${uh?.folio_no} - ${uh?.name}`,
          unitholder_name: uh?.name,
          folio_no: uh?.folio_no,
          account_title: uh?.account_title,
          account_no: uh?.account_no,
          bank_name: uh?.bank_name,
          branch_name: uh?.branch_name,
          systematic_investment: uh?.systematic_investment || {},
        }));
        setUnitholderOptions(options);
      } else {
        setUnitholderOptions([]);
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to search unitholders",
      );
    } finally {
      setUnitholderLoading(false);
    }
  };

  useEffect(() => {
    if (!fields.amc_code || viewMode === "view") return;
    const timeoutId = setTimeout(() => {
      if (unitholderSearchInput) {
        searchUnitholders(unitholderSearchInput, fields.amc_code);
      } else {
        if (fields.amc_code) fetchInitialUnitholders(fields.amc_code);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [unitholderSearchInput, fields.amc_code]);

  const handleAmcChange = async (selected: any) => {
    if (!selected) return;

    setFields((prev) => ({
      ...prev,
      amc_code: selected.value,
      amc_name: selected.name,
      fund_code: "",
      fund_name: "",
      account_number: "",
      account_title: "",
      bank: "",
      branch: "",
      counter_account_title: "",
      counter_account_number: "",
      counter_bank: "",
      counter_branch: "",
      nav: "",
      price: "",
      folio_no: "",
      unitholder_name: "",
    }));
    setFundOptions([]);
    setAccountOptions([]);
    setUnitholderAccountOptions([]);
    setUnitholderOptions([]);
    setUnitholderSearchInput("");

    try {
      const res = await getFundByAmc(email, selected.value);
      const options = res.data.data.map((f: any) => ({
        value: f.symbol_code,
        label: `${f.fund_name} (${f.symbol_code})`,
        nav: f.nav,
        face_value: f.face_value,
        fund_name: f.fund_name,
      }));
      setFundOptions(options);
      await fetchInitialUnitholders(selected.value);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load data");
    }
  };

  const handleFundChange = async (selected: any) => {
    if (!selected) return;

    setFields((prev) => ({
      ...prev,
      fund_code: selected.value,
      fund_name: selected.fund_name,
      nav: "",
      price: "",
      account_number: "",
      account_title: "",
      bank: "",
      branch: "",
      counter_account_title: "",
      counter_account_number: "",
      counter_bank: "",
      counter_branch: "",
    }));

    try {
      const res = await getAccountByFund(email, selected.value);
      const options = res.data.data.map((acc: any) => ({
        value: acc.account_code,
        label: `${acc.account_title} - ${acc.account_no}`,
        ...acc,
      }));
      setAccountOptions(options);
      setUnitholderAccountOptions(options);

      const { data } = await getDailyNavByDate(
        email,
        selected.value,
        fields.sale_date,
      );
      if (data?.data?.length > 0)
        updateField("nav", data?.data[0]?.Record?.offer_price || "0");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load accounts");
    }
  };

  const handleSaleDateChange = async (date: string) => {
    setNoNav(false);
    updateField("sale_date", date);

    if (fields.fund_code) {
      const { data } = await getDailyNavByDate(email, fields.fund_code, date);
      if (data?.data?.length > 0)
        updateField("nav", data?.data[0]?.Record?.offer_price || "0");
    }

    if (subFundsBlocks.length > 0) {
      await updateAllSubFundsNavs(date);
    }

    // Refresh CoA fund NAVs when date changes
    if (viewMode === "changeofallocation" && coaFunds.length > 0) {
      await updateCoaFundsNavs(date);
    }
  };

  //  Fetch accounts for a specific fund
  const fetchAccountsForFund = async (fundCode: string) => {
    try {
      const res = await getAccountByFund(email, fundCode);

      const accounts = res.data.data.map((acc: any) => ({
        value: acc.account_code,
        label: `${acc.account_title} - ${acc.account_no}`,
        account_no: acc.account_no,
        account_title: acc.account_title,
        account_purpose: acc.account_purpose || [],
        ...acc,
      }));

      setFundAccountsMap((prev) => ({
        ...prev,
        [fundCode]: accounts,
      }));

      return accounts;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to load accounts for fund ${fundCode}`,
      );
      return [];
    }
  };

  //  Auto-select Collection account
  const findCollectionAccount = (accounts: any[]) => {
    const collectionAccount = accounts.find(
      (acc) =>
        acc.account_purpose &&
        Array.isArray(acc.account_purpose) &&
        acc.account_purpose.includes("Collection"),
    );
    return collectionAccount || null;
  };

  //  Fetch NAV for a specific fund
  const fetchNavForFund = async (fundCode: string, date: string) => {
    try {
      const { data } = await getDailyNavByDate(email, fundCode, date);
      if (data?.data?.length > 0) {
        setOfferPrice(data.data[0]?.Record?.offer_price || "0");
        return {
          nav: parseFloat(data.data[0]?.Record?.nav),
          offer_price: parseFloat(data.data[0]?.Record?.offer_price),
          redemption_price: parseFloat(data.data[0]?.Record?.redemption_price),
        };
      }
      return "0";
    } catch (err: any) {
      console.error(`Failed to fetch NAV for ${fundCode}:`, err);
      return "0";
    }
  };

  const calculateUnits = (
    amount: string | number,
    nav: string | number,
  ): string => {
    const amountNum = parseFloat(amount.toString()).toFixed(4) || 0;
    const navNum = parseFloat(String(nav)).toFixed(4) || 0;

    if (navNum === 0) return "0.0000";

    const units: any = +amountNum / +navNum;
    return units.toFixed(4);
  };

  //  Update NAVs for all subfunds
  const updateAllSubFundsNavs = async (date: string) => {
    setNoNav(false);
    setSubFundsBlocks((prevBlocks: any) =>
      prevBlocks.map(async (block: any) => {
        if (!block.subFunds || block.subFunds.length === 0) return block;

        let total_nav: any = {};
        const updatedSubFunds = await Promise.all(
          block.subFunds.map(async (fund: any) => {
            if (!fund.fund_code) return fund;

            const navData: any = await fetchNavForFund(fund.fund_code, date);

            const nav =
              viewMode === "withdrawal"
                ? navData?.redemption_price
                : viewMode === "investment"
                  ? navData?.offer_price
                  : viewMode === "changeofallocation"
                    ? fund?.allocation_type &&
                      fund.allocation_type === "conversionin"
                      ? navData.offer_price
                      : navData.redemption_price
                    : "0";
            total_nav[fund.type_VPS] = isNaN(nav) ? 0 : nav;

            const units = calculateUnits(fund.amount || "0", nav);

            return {
              ...fund,
              nav,
              ...(viewMode === "changeofallocation" && {
                offer_price: navData.offer_price,
                redemption_price: navData.redemption_price,
              }),
              units,
            };
          }),
        );

        if (Object.values(total_nav).every((value) => value === 0)) {
          setNoNav(true);
        }

        return {
          ...block,
          subFunds: updatedSubFunds,
        };
      }),
    );
  };

  //  Handle account selection for subfund
  const handleSubFundAccountChange = (
    blockId: string,
    fundIndex: number,
    selectedAccount: any,
  ) => {
    setErrors({});
    setSubFundsBlocks((prevBlocks: any) =>
      prevBlocks.map((block: any) => {
        if (block.id !== blockId) return block;

        const updatedSubFunds = [...block.subFunds];
        updatedSubFunds[fundIndex] = {
          ...updatedSubFunds[fundIndex],
          selected_account: selectedAccount,
          account_no: selectedAccount?.account_no || "",
        };

        return {
          ...block,
          subFunds: updatedSubFunds,
        };
      }),
    );
  };

  const handleAccountChange = (selected: any) => {
    if (!selected) return;
    setFields((prev) => ({
      ...prev,
      account_number: selected.account_no,
      account_title: selected.account_title,
      bank: selected.bank_name,
      branch: `${selected.branch_name} — ${selected.city || ""}`,
    }));
  };

  // Refresh NAVs for all coaFunds rows when date changes
  const updateCoaFundsNavs = async (date: string) => {
    if (coaFunds.length === 0) return;

    const updatedFunds = await Promise.all(
      coaFunds.map(async (item) => {
        const updated = { ...item };

        // Refresh from_fund redemption NAV
        if (item.from_fund) {
          const fromNavData: any = await fetchNavForFund(item.from_fund, date);
          updated.from_fund_nav =
            fromNavData?.redemption_price || item.from_fund_nav;
        }

        // Refresh to_fund offer NAV
        if (item.to_fund) {
          const toNavData: any = await fetchNavForFund(item.to_fund, date);
          updated.to_fund_nav = toNavData?.offer_price || item.to_fund_nav;
        }

        // Recalculate amounts/units with new NAVs
        if (updated.from_fund_unit > 0 && updated.from_fund_nav > 0) {
          updated.amount = updated.from_fund_unit * updated.from_fund_nav;
          if (updated.to_fund && updated.to_fund_nav > 0) {
            updated.to_fund_unit = updated.amount / updated.to_fund_nav;
          }
        } else if (updated.amount > 0 && updated.from_fund_nav > 0) {
          updated.from_fund_unit = updated.amount / updated.from_fund_nav;
          if (updated.to_fund && updated.to_fund_nav > 0) {
            updated.to_fund_unit = updated.amount / updated.to_fund_nav;
          }
        }

        return updated;
      }),
    );

    setCoaFunds(updatedFunds);
  };

  const updateNav = async () => {
    setNavLoading(true);
    await handleSaleDateChange(fields.sale_date);
    await handleUnitholderChange(
      unitholderOptions.find((opt) => opt.folio_no === fields.folio_no),
    );
    setNavLoading(false);
  };

  const validate = () => {
    const required = ["amc_code", "folio_no", "unitholder_name"];

    if (!amount && viewMode === "withdrawal") {
      setIndexedError("withdrawal", "Amount is 0 Can't create transaction");
      return false;
    }

    for (const key of required) {
      if (
        !fields[key as keyof InvestmentTransactionFields]?.toString().trim()
      ) {
        return false;
      }
    }
    return true;
  };

  const validateInvestmentBlock = (block): boolean => {
    let valid = true;
    let totalUnits = 0;
    block[0].subFunds.forEach((fund) => {
      if (!fund.selected_account) {
        setIndexedError(`${fund.fund_code}:account`, "please select account");
        valid = false;
      }
      totalUnits += parseFloat(fund.uints);
    });

    if (!totalUnits && totalUnits < 1) {
      setIndexedError(
        "units_0",
        "Can't Create Transaction when sum of all the fund units is zero",
      );
      return false;
    }

    return valid;
  };
  const handleSubmit = async () => {
    setErrorShow(false);

    if (!validate()) {
      setErrorShow(true);
      toast.error("Please fill all required fields.");
      return;
    }
    if (noNav) {
      toast.error("Can't create Transaction: Nav is Zero");
      return;
    }

    setLoading(true);
    try {
      if (Object.values(errors).some((value) => value !== null)) {
        toast.error("Kindly Resolve Error in Systematic investment");
        setLoading(false);
        return;
      }

      // --- Change of Allocation: separate path, return early ---
      if (viewMode === "changeofallocation") {
        if (!validateCoA()) {
          setLoading(false);
          return;
        }

        const payload = buildCoaPayload();

        // Ensure total movement is valid
        const totalFromAmount = payload.funds.reduce(
          (sum, f) => sum + f.amount,
          0,
        );
        if (totalFromAmount <= 0) {
          toast.error("Total transfer amount must be greater than zero");
          setLoading(false);
          return;
        }

        // Build the systematic_investment specifically for CoA
        const systematicInvestment = {
          allocation_scheme: typeVPS,
          investment_amount: totalFromAmount,
          previous_allocation: currentSubFundsBlock,
          investment_blocks: [
            {
              ...currentSubFundsBlock[0],
              subFunds: payload.funds,
            },
          ],
        };

        const response = await addInvestmentTransaction(email, {
          ...fields,
          txnr_type: "changeofallocation",
          type: "debit", // CoA is treated as a debit/re-investment cycle
          from_scheme: fromScheme,
          to_scheme: toScheme || "",
          systematic_investment: systematicInvestment,
          gross_amount: totalFromAmount.toFixed(4),
          net_amount: totalFromAmount.toFixed(4),
          detail: payload.detail,
        });

        if (response?.data?.success) {
          toast.success("Change of Allocation submitted successfully!");
          setTimeout(() => history.push("/admin/post-transaction"), 1000);
        }
        setLoading(false);
        return;
      }

      // --- Investment / Withdrawal path ---
      const systematicInvestment: any = {
        total_block_invest: subFundsBlocks[0].parentProportion,
        allocation_scheme: typeVPS,
        investment_amount: amount,
        investment_blocks: subFundsBlocks,
      };

      const isValid = validateInvestmentBlock(subFundsBlocks);
      if (!isValid) {
        setLoading(false);
        return;
      }

      const total = {
        amount_in: 0,
        amount_out: 0,
        units_in: 0,
        units_out: 0,
      };

      const response = await addInvestmentTransaction(email, {
        ...fields,
        net_amount: total.amount_in.toFixed(4),
        gross_amount: total.amount_in.toFixed(4),
        ...(viewMode === "investment" && {
          txnr_type: "investment",
          type: "debit",
        }),
        ...(viewMode === "withdrawal" && {
          txnr_type: "withdrawal",
          type: "debit",
        }),
        systematic_investment: systematicInvestment,
      });

      if (response?.data?.success) {
        toast.success("Transaction submitted successfully!");
        setTimeout(() => {
          history.push("/admin/post-transaction");
        }, 1000);
      } else {
        toast.error(response?.data?.message || "Submission failed");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const getErrorClass = (value: string) =>
    errorShow && !value ? "required-border" : "";

  const updateField = (
    key: keyof InvestmentTransactionFields,
    value: string | number,
  ) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleUnitholderChange = async (selected: any) => {
    setNoNav(false);
    if (!selected) {
      setFields((prev) => ({
        ...prev,
        folio_no: "",
        unitholder_name: "",
        counter_account_title: "",
        counter_account_number: "",
        counter_bank: "",
        counter_branch: "",
      }));
      setSubFundsBlocks([]);
      setTypeVPS("");
      return;
    }

    setTotalUnits(
      parseFloat(
        selected?.systematic_investment?.investment_blocks[0]?.total_holding ||
          0,
      ).toFixed(4),
    );

    const savedBlocks =
      selected?.systematic_investment?.investment_blocks || [];
    const allocationScheme =
      selected?.systematic_investment?.allocation_scheme || "";

    setTypeVPS(allocationScheme);
    if (viewMode === "changeofallocation") setFromScheme(allocationScheme);

    setFields((prev) => ({
      ...prev,
      folio_no: selected.folio_no,
      unitholder_name: selected.unitholder_name,
      counter_account_title: selected.account_title,
      counter_account_number: selected.account_no,
      counter_bank: selected.bank_name,
      counter_branch: selected.branch_name,
      systematic_investment: selected?.systematic_investment || {},
    }));

    if (location.pathname.includes("view-vps-transactions")) {
      selected.systematic_investment.investment_blocks[0].total_amount =
        selected?.systematic_investment?.investment_amount;
      setSubFundsBlocks(selected?.systematic_investment?.investment_blocks);
      return;
    }

    const parentFund =
      selected?.systematic_investment?.investment_blocks[0].selectedParentFund
        .value;

    if (["investment", "withdrawal"].includes(viewMode) && parentFund)
      await fetchParentFundAccounts(parentFund);

    SetCurrentSubFundsBlock(savedBlocks);

    const amountToSet = ["withdrawal", "changeofallocation"].includes(viewMode)
      ? 0.0
      : selected?.systematic_investment?.investment_amount || 0;

    setCurrAmount(selected?.systematic_investment?.investment_amount || 0);

    setAmount(amountToSet);
    if (!allocationScheme || savedBlocks.length === 0) {
      setSubFundsBlocks([]);
      return;
    }

    let total_investement = 0;
    let total_amount: any = 0;
    let total_nav: any = {};
    // Rebuild each block with fresh accounts + NAVs for sub-funds
    const rebuiltBlocks = await Promise.all(
      savedBlocks.map(async (block: any) => {
        const parentFundCode = block.fund || block.fund_code;
        if (!parentFundCode) return block;

        // Reconstruct subFunds with fresh data
        const newSubFunds = await Promise.all(
          (block.subFunds || []).map(async (subFund: any) => {
            const fundCode = subFund.fund_code;

            if (!fundCode) {
              return { ...subFund, accounts: [], selected_account: null };
            }

            subFund.holding = parseFloat(
              parseFloat(subFund.holding).toFixed(4),
            );

            // Fetch accounts for this child fund
            const accounts = await fetchAccountsForFund(fundCode);
            const collectionAccount = findCollectionAccount(accounts);

            // Fetch NAV on current sale_date
            const navData: any = await fetchNavForFund(
              fundCode,
              fields.sale_date,
            );
            const nav = parseFloat(
              viewMode === "withdrawal"
                ? navData?.redemption_price
                : viewMode === "investment"
                  ? navData?.offer_price
                  : viewMode === "changeofallocation"
                    ? navData.redemption_price
                    : 0,
            );

            total_nav[subFund?.type_VPS] = isNaN(nav) ? 0 : +nav;

            total_amount += nav * (subFund?.holding || 0);
            // Recalculate units
            const amountValue =
              parseFloat(amountToSet) * (parseFloat(subFund.proportion) / 100);
            subFund.amount = amountValue.toFixed(4);
            const units = calculateUnits(amountValue, nav);
            const holding_amount = subFund?.holding * nav;
            total_investement += subFund.holding * nav;

            const updatedSubFund = {
              ...subFund,
              ...(viewMode === "changeofallocation" && {
                counter_fund: { value: "", label: "" },
                holding_amount,
                amount_in: 0,
                amount_out: 0,
              }),
              accounts,
              selected_account: collectionAccount,
              account_code: collectionAccount?.value || "",
              account_no: collectionAccount?.account_no || "",
              account_title: collectionAccount?.account_title || "",

              nav,
              ...(viewMode === "changeofallocation" && {
                offer_price: navData.offer_price,
                redemption_price: navData.redemption_price,
              }),
              units,
            };
            return updatedSubFund;
          }),
        );

        block.total_amount = calculateBlockTotal(newSubFunds, "amount");
        return {
          ...block,
          subFunds: newSubFunds,
        };
      }),
    );

    setTotalAmount(parseFloat(total_amount || 0).toFixed(4));

    if (Object.values(total_nav).every((value) => value === 0)) {
      setNoNav(true);
    }

    if (totalInvestment === 0) {
      setTotalInvesment(total_investement);
    }
    setSubFundsBlocks(rebuiltBlocks);
  };

  const handleUnitholderInputChange = (inputValue: string) => {
    setUnitholderSearchInput(inputValue);
  };

  const renderRange = (ValueIs) => {
    const filteredType = typeRatioSystematic?.find(
      (ele) => ele?.label === typeVPS,
    );
    if (filteredType && ValueIs) {
      return filteredType.ratio[ValueIs] || "";
    }
    return "";
  };

  const calculateBlockTotal = (subFunds, key = "proportion") => {
    return subFunds.reduce((total, fund) => {
      const proportion = parseFloat(fund[key]) || 0;
      return total + proportion;
    }, 0);
  };

  const removeSubFundsBlock = (blockId) => {
    if (subFundsBlocks.length > 1) {
      setSubFundsBlocks(subFundsBlocks.filter((block) => block.id !== blockId));
    }
  };

  // we remove code because of new changes for withdrawal logic after confirmation from QA we will remove below function and
  // commented useEffect
  const onAmountChange = (amount: number) => {
    if (amount < 0 || subFundsBlocks.length < 1) return;

    if (viewMode === "withdrawal" && isManualUpdate.current) return;

    let totalUnits = 0;
    const fundsWithExcess: Array<{
      blockIndex: number;
      fundIndex: number;
      excessAmount: number;
      excessUnits: number;
      fund: any;
    }> = [];

    const fundsWithCapacity: Array<{
      blockIndex: number;
      fundIndex: number;
      availableAmount: number;
      availableUnits: number;
      fund: any;
    }> = [];

    // Helper for float comparison
    const floatEquals = (a: number, b: number, tolerance = 0.0001) =>
      Math.abs(a - b) < tolerance;

    // --- Step 1: Identify funds with excess or capacity ---
    const initialBlocks = subFundsBlocks.map(
      (block: any, blockIndex: number) => {
        if (block.subFunds.length < 1) return block;

        const updatedSubFunds = block.subFunds.map(
          (fund: any, fundIndex: number) => {
            if (fund.proportion <= 0) return fund;

            const newAmount = amount * (parseFloat(fund.proportion) / 100);
            const nav = parseFloat(fund.nav) || 0;
            const units = parseFloat(calculateUnits(newAmount, nav));
            const currentHolding =
              parseFloat(parseFloat(fund.holding).toFixed(4)) || 0;

            if (viewMode === "withdrawal") {
              if (
                units > currentHolding &&
                !floatEquals(units, currentHolding)
              ) {
                // Fund exceeds its holding
                const maxAllowedUnits = currentHolding;
                const maxAllowedAmount = maxAllowedUnits * nav;
                const excessUnits = units - maxAllowedUnits;
                const excessAmount = excessUnits * nav;

                fundsWithExcess.push({
                  blockIndex,
                  fundIndex,
                  excessAmount,
                  excessUnits,
                  fund,
                });
              } else {
                // Fund has capacity left
                const maxAllowedUnits = currentHolding;
                const availableUnits = maxAllowedUnits - units;
                const availableAmount = availableUnits * nav;

                if (availableAmount > 0.01) {
                  fundsWithCapacity.push({
                    blockIndex,
                    fundIndex,
                    availableAmount,
                    availableUnits,
                    fund,
                  });
                }
              }
            }

            return {
              ...fund,
              amount: newAmount.toFixed(4),
              units: units.toFixed(4),
            };
          },
        );

        return { ...block, subFunds: updatedSubFunds };
      },
    );

    // --- Step 2: Redistribute excess between funds ---
    let updatedBlocks = [...initialBlocks];
    let remainingExcess = 0;

    if (viewMode === "withdrawal" && fundsWithExcess.length > 0) {
      const totalExcessAmount = fundsWithExcess.reduce(
        (sum, item) => sum + item.excessAmount,
        0,
      );
      const totalCapacityAmount = fundsWithCapacity.reduce(
        (sum, item) => sum + item.availableAmount,
        0,
      );

      // Case 1: Sufficient capacity to absorb all excess
      if (totalCapacityAmount >= totalExcessAmount) {
        // Cap funds at max allowed holding
        fundsWithExcess.forEach(({ blockIndex, fundIndex, fund }) => {
          const maxUnits = parseFloat(fund.holding).toFixed(4);
          const nav = parseFloat(fund.nav) || 0;
          const maxAmount = parseFloat(maxUnits) * nav;

          updatedBlocks[blockIndex].subFunds[fundIndex] = {
            ...fund,
            amount: maxAmount.toFixed(4),
            units: parseFloat(maxUnits).toFixed(4),
          };
        });

        // Redistribute excess proportionally to available funds
        fundsWithCapacity.forEach(
          ({ blockIndex, fundIndex, availableAmount, fund }) => {
            const proportion = availableAmount / totalCapacityAmount;
            const additionalAmount = totalExcessAmount * proportion;
            const nav = parseFloat(fund.nav) || 0;
            const currentAmount = parseFloat(
              updatedBlocks[blockIndex].subFunds[fundIndex].amount,
            );
            const newAmount = currentAmount + additionalAmount;

            const maxHolding = parseFloat(fund.holding);
            const newUnits = Math.min(newAmount / nav, maxHolding);
            const finalAmount = newUnits * nav;

            updatedBlocks[blockIndex].subFunds[fundIndex] = {
              ...updatedBlocks[blockIndex].subFunds[fundIndex],
              amount: finalAmount.toFixed(4),
              units: newUnits.toFixed(4),
            };
          },
        );

        remainingExcess = 0;
      }
      // Case 2: Not enough capacity available
      else {
        remainingExcess = totalExcessAmount - totalCapacityAmount;

        // Cap funds with excess
        fundsWithExcess.forEach(({ blockIndex, fundIndex, fund }) => {
          const maxUnits = parseFloat(fund.holding).toFixed(4);
          const nav = parseFloat(fund.nav) || 0;
          const maxAmount = parseFloat(maxUnits) * nav;

          updatedBlocks[blockIndex].subFunds[fundIndex] = {
            ...fund,
            amount: maxAmount.toFixed(4),
            units: parseFloat(maxUnits).toFixed(4),
          };
        });

        // Use up all capacity proportionally
        fundsWithCapacity.forEach(
          ({ blockIndex, fundIndex, availableAmount, fund }) => {
            const proportion = availableAmount / totalCapacityAmount;
            const additionalAmount = totalCapacityAmount * proportion;
            const nav = parseFloat(fund.nav) || 0;
            const currentAmount = parseFloat(
              updatedBlocks[blockIndex].subFunds[fundIndex].amount,
            );
            const newAmount = currentAmount + additionalAmount;

            const maxHolding = parseFloat(fund.holding);
            const newUnits = Math.min(newAmount / nav, maxHolding);
            const finalAmount = newUnits * nav;

            updatedBlocks[blockIndex].subFunds[fundIndex] = {
              ...updatedBlocks[blockIndex].subFunds[fundIndex],
              amount: finalAmount.toFixed(4),
              units: newUnits.toFixed(4),
            };
          },
        );
      }
    }

    // --- Step 3: Recalculate totals ---
    updatedBlocks = updatedBlocks.map((block: any) => {
      const blockUnits = block.subFunds.reduce(
        (sum: number, fund: any) => sum + parseFloat(fund.units || 0),
        0,
      );

      totalUnits += blockUnits;
      block.total_amount = calculateBlockTotal(block.subFunds, "amount");

      return block;
    });

    // --- Step 4: Update states ---
    setSubFundsBlocks(updatedBlocks);
    setUnits(totalUnits.toFixed(4));

    // --- Step 5: Handle errors ---
    if (remainingExcess > 0.01) {
      setErrors((prev) => ({
        ...prev,
        withdrawal: `Unable to redistribute ${remainingExcess.toFixed(
          2,
        )} due to insufficient holdings across all funds.`,
      }));
    } else {
      setErrors((prev) => {
        const { withdrawal, ...rest } = prev;
        return rest;
      });
    }
  };

  // useEffect(() => {
  //   if (["changeofallocation", "view", "investment"].includes(viewMode)) return;

  //   if (isManualUpdate.current) {
  //     isManualUpdate.current = false; // Reset the flag
  //     return;
  //   }

  //   if (amount > 0) {
  //     onAmountChange(amount);
  //   } else {
  //     setUnits("");
  //   }
  // }, [amount]);

  const setIndexedError = (index: number | string, message: string | null) => {
    setErrors((prevErrors) => {
      if (!message) {
        const { [index]: removed, ...rest } = prevErrors;
        return rest;
      }
      return { ...prevErrors, [index]: message };
    });
  };

  const handleCustomCheckboxChange = (
    blockId: any,
    fundIndex: any,
    isChecked: any,
  ) => {
    setSubFundsBlocks((prevBlocks: any) =>
      prevBlocks.map((block: any) => {
        if (block.id !== blockId || !block.subFunds) {
          return block;
        }

        const updatedSubFunds: any = block.subFunds.map((fund, index) => {
          if (index === fundIndex) {
            const newProportion = isChecked ? "100" : "0";
            const newAmount = isChecked ? amount : 0;
            const units = calculateUnits(newAmount, fund.nav || "0");

            return {
              ...fund,
              proportion: newProportion,
              amount: newAmount.toFixed(4),
              units,
            };
          } else if (isChecked) {
            return {
              ...fund,
              proportion: "0",
              amount: "0.00",
              units: "0.0000",
            };
          }
          return fund;
        });

        const newBlockTotal = calculateBlockTotal(updatedSubFunds);
        const newBlockAmountTotal = calculateBlockTotal(
          updatedSubFunds,
          "amount",
        );

        return {
          ...block,
          subFunds: updatedSubFunds,
          parentProportion: newBlockTotal,
          total_amount: newBlockAmountTotal,
        };
      }),
    );
  };

  const getfundByAmcCode = async (code: string) => {
    setFundLoading(true);
    try {
      const response = await getFundByAmc(email, code, {
        fund_type: "sub_fund",
      });
      setAllFunds(response.data.data);
      if (response.data.data.length > 0) {
        setFundLoading(false);
      } else {
        setFundLoading(true);
      }
    } catch (err: any) {
      if (err.response !== undefined) {
        toast.error(err.response.data.message);
      } else {
        toast.error(err.message);
      }
    }
  };

  useEffect(() => {
    if (viewMode === "view") return;
    if (fields.amc_code) getfundByAmcCode(fields.amc_code);
  }, [fields.amc_code]);

  useEffect(() => {
    if (id) {
      const [txn_id, route] = id.split(":");
      fetchTransactionDetails(txn_id);
      if (route === "compliance") setShowApproval(true);
    }
  }, [id]);

  const fetchTransactionDetails = async (transactionId: string) => {
    try {
      setTxnLoading(true);
      const res = await getInvestmentTransactionById(transactionId);
      if (res?.data?.success) {
        const txn = res.data.data;
        setFields({
          ...txn,
        });
        setTypeVPS(txn?.systematic_investment?.allocation_scheme || "");

        setAmount(txn?.systematic_investment?.investment_amount || 0);
        if (txn) {
          const unitholderData = {
            value: txn.folio_no,
            label: `${txn.folio_no} - ${txn.unitholder_name}`,
            unitholder_name: txn.unitholder_name,
            folio_no: txn.folio_no,
            account_title: txn.counter_account_title,
            account_no: txn.counter_account_number,
            bank_name: txn.counter_bank,
            branch_name: txn.counter_branch,
            systematic_investment: txn.systematic_investment || {},
          };
          if (txn.txnr_type === "changeofallocation") {
            setCurrAmount(txn?.systematic_investment?.investment_amount || 0);
            setFromScheme(
              txn?.from_scheme || txn?.systematic_investment?.to_scheme || "",
            );

            if (txn.to_scheme) setToScheme(txn.to_scheme);
            const coaFunds =
              txn?.systematic_investment?.investment_blocks[0].subFunds;
            const processedCoaFunds = coaFunds.map((fund) => ({
              ...fund,
              selected_from_account: {
                value: fund.from_fund_acc,
                label: fund.from_account_title,
              },
              selected_to_account: {
                value: fund.to_fund_acc,
                label: fund.to_account_title,
              },
            }));
            setCoaFunds(processedCoaFunds);
            SetCurrentSubFundsBlock(
              txn?.systematic_investment?.previous_allocation,
            );
          }
          setUnitholderOptions([unitholderData]);
          handleUnitholderChange(unitholderData);
        }
        toast.success("Transaction details loaded successfully!");
      } else {
        toast.error("Failed to fetch transaction details.");
      }
      setTxnLoading(false);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          "An error occurred while fetching transaction details.",
      );
      setTxnLoading(false);
    }
  };

  const selectedSystematicVPSType = useMemo(() => {
    return typeVPS
      ? {
          value: typeVPS,
          label: typeVPS,
        }
      : null;
  }, [typeVPS]);

  const handleClearCounterFund = (blockId, fundIndex) => {
    setSubFundsBlocks((prevBlocks) =>
      prevBlocks.map((block) => {
        if (block.id !== blockId) return block;

        const updatedSubFunds = [...block.subFunds];
        const fundBlock = { ...updatedSubFunds[fundIndex] };

        // Get the current counter fund if it exists
        const counterFundCode = fundBlock.counter_fund?.value;
        if (!counterFundCode) return block;

        // Find counter fund index
        const counterIndex = updatedSubFunds.findIndex(
          (item) => item.fund_code === counterFundCode,
        );

        const currentFunds = currentSubFundsBlock.find(
          (b) => b.id === blockId,
        ).subFunds;

        const currentBlockData = currentFunds[fundIndex];

        if (counterIndex > -1) {
          const counterFundBlock = { ...updatedSubFunds[counterIndex] };

          // Get original data from saved allocation
          const currentCounterBlock = currentFunds[counterIndex];

          // Subtract current fund's amount and units from counter fund
          counterFundBlock.amount = Math.max(
            0,
            (counterFundBlock.amount || 0) - (fundBlock.amount || 0),
          );
          counterFundBlock.units = Math.max(
            0,
            (counterFundBlock.units || 0) - (fundBlock.units || 0),
          );

          // Reset counter fund proportion to original if no more allocation
          if (counterFundBlock.amount === 0) {
            counterFundBlock.proportion = parseFloat(
              currentCounterBlock.proportion,
            );
            delete counterFundBlock.allocation_type;
            delete counterFundBlock.sending_funds;
          } else {
            counterFundBlock.sending_funds =
              counterFundBlock.sending_funds.filter(
                (item: string) => item === currentBlockData.fund_code,
              );
          }

          updatedSubFunds[counterIndex] = counterFundBlock;
        }

        // Reset current fund to original state
        fundBlock.amount = 0;
        fundBlock.units = 0;
        fundBlock.proportion = parseFloat(currentBlockData.proportion);
        delete fundBlock.allocation_type;

        // Clear the counter fund selection
        fundBlock.counter_fund = { value: "", label: "" };

        updatedSubFunds[fundIndex] = fundBlock;

        return { ...block, subFunds: updatedSubFunds };
      }),
    );

    setErrors({});
  };

  const handleChangeAllocation = (
    blockId,
    fundIndex,
    value,
    currentRange,
    counterIndex,
  ) => {
    setErrors({});
    setSubFundsBlocks((prevBlocks) =>
      prevBlocks.map((block) => {
        if (block.id !== blockId) return block;

        const updatedSubFunds = [...block.subFunds];
        const fundBlock = { ...updatedSubFunds[fundIndex] };

        const currentFunds = currentSubFundsBlock.find(
          (b) => b.id === blockId,
        ).subFunds;

        const counterFundBlock =
          counterIndex > -1 ? { ...updatedSubFunds[counterIndex] } : null;

        // Unitholder Dbs Saved Allocation Data
        const currentBlockData = currentFunds[fundIndex];
        const currentCounterBlock =
          counterIndex > -1 ? currentFunds[counterIndex] : null;

        const originalProportion = parseFloat(currentBlockData.proportion);

        const counterOriginalProp =
          counterIndex > -1 ? parseFloat(currentCounterBlock.proportion) : 0;

        const [minRange, maxRange] = currentRange.map(Number);

        // Update raw proportion so typing continues
        fundBlock.proportion = value;
        updatedSubFunds[fundIndex] = fundBlock;

        if (value > maxRange || value < minRange) {
          return { ...block, subFunds: updatedSubFunds };
        }

        // Prevent unnecessary update loop
        if (!isNaN(originalProportion) && originalProportion === value) {
          // Resetting the States
          fundBlock.units = 0;
          fundBlock.amount = 0;
          fundBlock.proportion = originalProportion;
          delete fundBlock.allocation_type;
          updatedSubFunds[fundIndex] = fundBlock;

          if (counterFundBlock && counterIndex > -1) {
            // Resetting the States
            counterFundBlock.units = 0;
            counterFundBlock.amount = 0;
            delete counterFundBlock.allocation_type;
            counterFundBlock.proportion = counterOriginalProp;
            updatedSubFunds[counterIndex] = counterFundBlock;
          }
          return { ...block, subFunds: updatedSubFunds };
        }

        // 2. Determine In or Out based on increase/decrease
        const isIncreasing = value > originalProportion;
        fundBlock.allocation_type = isIncreasing
          ? "conversionin"
          : "conversionout";

        // Update proportion
        const proportionToAllocate = Math.abs(originalProportion - value);

        const investmentAmount = fundBlock.holding * fundBlock.nav;

        const amountToProcess =
          ((investmentAmount / (originalProportion / 100)) *
            proportionToAllocate) /
          100;

        fundBlock.amount = amountToProcess;
        fundBlock.units = amountToProcess / fundBlock.nav;

        if (counterFundBlock) {
          // Calculate its updated proportion
          const prevType = counterFundBlock?.allocation_type || "";
          counterFundBlock.allocation_type = isIncreasing
            ? "conversionout"
            : "conversionin";

          if (prevType && prevType !== counterFundBlock.allocation_type) {
            toast.error(
              `Invalid Allocation for  ${counterFundBlock.fund_code}`,
            );
            return block;
          }

          let counterProportion = counterOriginalProp;

          if (counterFundBlock.allocation_type === "conversionin") {
            if (!counterFundBlock?.sending_funds)
              counterFundBlock.sending_funds = [fundBlock.fund_code];
            else if (
              !counterFundBlock.sending_funds.includes(fundBlock.fund_code)
            ) {
              counterProportion = counterFundBlock.proportion;
              counterFundBlock.sending_funds.push(fundBlock.fund_code);
            }
          }

          const counterNewProportion = isIncreasing
            ? counterProportion - proportionToAllocate
            : counterProportion + proportionToAllocate;

          // Get counter range
          const [counterMin, counterMax] = String(
            renderRange(counterFundBlock?.type_VPS),
          )
            .replace("%", "")
            .split("-")
            .map(Number);

          // If counter goes out of its range → STOP EVERYTHING
          if (
            counterNewProportion < counterMin ||
            counterNewProportion > counterMax
          ) {
            toast.warn("Counter Fund Allocation Limits Violated");
            // return block; // Discard changes
          }

          // Proceed with counter movement
          counterFundBlock.proportion = counterNewProportion;
          counterFundBlock.amount =
            parseFloat(counterFundBlock.amount) + amountToProcess;
          counterFundBlock.units =
            parseFloat(counterFundBlock.units) +
            amountToProcess / counterFundBlock.nav;

          updatedSubFunds[counterIndex] = counterFundBlock;
        }

        updatedSubFunds[fundIndex] = fundBlock;

        return { ...block, subFunds: updatedSubFunds };
      }),
    );
  };

  // Update the handleSubFundChange to call handleClearCounterFund when clearing
  const handleSubFundChange = (blockId, fundIndex, selectedFund) => {
    // If clearing the selection (empty value)
    if (!selectedFund?.value) {
      handleClearCounterFund(blockId, fundIndex);
      return;
    }
    // Existing logic for selecting a fund
    setSubFundsBlocks((prevBlocks) =>
      prevBlocks.map((block) => {
        if (block.id !== blockId) return block;

        const updatedSubFunds = [...block.subFunds];
        updatedSubFunds[fundIndex] = {
          ...updatedSubFunds[fundIndex],
          counter_fund: selectedFund,
        };

        return { ...block, subFunds: updatedSubFunds };
      }),
    );
  };

  // below logic is for the Change of Allocation Amount
  const handleAmountChange = (blockId, fundIndex, amount, type) => {
    setSubFundsBlocks((prevBlocks) =>
      prevBlocks.map((block) => {
        if (block.id !== blockId) return block;

        const updatedSubFunds = [...block.subFunds];
        const fundBlock = { ...updatedSubFunds[fundIndex] }; // Create a new object

        // Check if value actually changed to prevent infinite loop
        if (type === "in" && fundBlock.amount_in === amount) {
          return block;
        }
        if (type === "out" && fundBlock.amount_out === amount) {
          return block;
        }

        const nav =
          type === "out" ? fundBlock.redemption_price : fundBlock.offer_price;
        fundBlock.holding = parseFloat(fundBlock.holding).toFixed(4);
        // Update the appropriate field
        if (type === "in") {
          fundBlock.amount_in = amount;
        } else {
          if (
            nav &&
            amount >
              parseFloat((parseFloat(fundBlock.holding) * nav).toFixed(4))
          ) {
            toast.warn("Invalid allocation out amount");
            return block;
          }
          fundBlock.amount_out = amount;
        }

        // Calculate units and set allocation type
        fundBlock.units = amount / parseFloat(nav);
        fundBlock.allocation_type =
          type === "out" ? "conversionout" : "conversionin";

        // Update the subfunds array
        updatedSubFunds[fundIndex] = fundBlock;

        return { ...block, subFunds: updatedSubFunds };
      }),
    );
  };

  const onSubmitStatus = async (data: any) => {
    try {
      const response = await updateVPSTxn(data);

      if (response.data?.status !== 200) {
        toast.error(response.data?.message || "Something went wrong");
        const errorData: any = [
          {
            status: response.data.status,
            message: response.data?.message || "",
            messages: response.data?.messages || response.data || [],
          },
        ];
        setVpsErrorMessages(errorData);
        if (response.data?.messages?.length > 0) setShowVpsErrors(true);
      } else {
        toast.success(response.data?.message || "Transaction Status Updated");
        setTimeout(() => {
          history.push("/admin/compliance");
        }, 500);
      }
    } catch (error: any) {
      toast.error(error.message || "Something went wrong");
    }
  };

  const handleManualWithdrawalAmount = (
    blockId: string,
    fundIndex: number,
    newAmount: number,
  ) => {
    isManualUpdate.current = true;
    setSubFundsBlocks((prevBlocks: any) => {
      let globalTotalAmount = 0;
      let globalTotalUnits = 0;

      const updatedBlocks = prevBlocks.map((block: any) => {
        if (block.id !== blockId) {
          globalTotalAmount += parseFloat(block.total_amount || 0);
          const blockUnits = block.subFunds.reduce(
            (sum, f) => sum + parseFloat(f.units || 0),
            0,
          );
          globalTotalUnits += blockUnits;
          return block;
        }

        const updatedSubFunds = [...block.subFunds];
        const fund = { ...updatedSubFunds[fundIndex] };

        const nav =
          parseFloat(viewMode === "withdrawal" ? fund.nav : fund.offer_price) ||
          0;
        const holding = parseFloat(fund.holding) || 0;
        const maxAmount = parseFloat((holding * nav).toFixed(4));

        let processedAmount = newAmount;

        if (viewMode === "withdrawal" && processedAmount > maxAmount) {
          processedAmount = maxAmount;
          toast.warn(`Amount capped at maximum holding value: ${maxAmount}`);
        }

        const units = calculateUnits(processedAmount, nav);

        updatedSubFunds[fundIndex] = {
          ...fund,
          amount: processedAmount.toFixed(4),
          units: units,
        };

        const blockTotal = updatedSubFunds.reduce(
          (sum, f) => sum + parseFloat(f.amount || 0),
          0,
        );

        globalTotalAmount += blockTotal;
        const currentBlockUnits = updatedSubFunds.reduce(
          (sum, f) => sum + parseFloat(f.units || 0),
          0,
        );
        globalTotalUnits += currentBlockUnits;

        return {
          ...block,
          subFunds: updatedSubFunds,
          total_amount: blockTotal.toFixed(4),
        };
      });

      setAmount(globalTotalAmount.toFixed(4));
      setUnits(globalTotalUnits.toFixed(4));

      return updatedBlocks;
    });
  };

  const handleManualWithdrawalUnits = (
    blockId: string,
    fundIndex: number,
    newUnits: number,
  ) => {
    isManualUpdate.current = true;
    setSubFundsBlocks((prevBlocks: any) => {
      let globalTotalAmount = 0;
      let globalTotalUnits = 0;

      const updatedBlocks = prevBlocks.map((block: any) => {
        if (block.id !== blockId) {
          // Accumulate other blocks' totals unchanged
          globalTotalAmount += parseFloat(block.total_amount || 0);
          const blockUnits = block.subFunds.reduce(
            (sum: number, f: any) => sum + parseFloat(f.units || 0),
            0,
          );
          globalTotalUnits += blockUnits;
          return block;
        }

        const updatedSubFunds = [...block.subFunds];
        const fund = { ...updatedSubFunds[fundIndex] };

        const nav = parseFloat(fund.nav) || 0;
        const holding = parseFloat(fund.holding) || 0; // holding is in units

        // Cap: cannot withdraw more units than held
        let processedUnits = newUnits;
        if (processedUnits > holding) {
          processedUnits = holding;
          // toast is imported in the hook already; import { toast } from "react-toastify"
          toast.warn(`Units capped at maximum holding: ${holding}`);
        }

        // Derive amount from units × NAV
        const derivedAmount = parseFloat((processedUnits * nav).toFixed(4));

        updatedSubFunds[fundIndex] = {
          ...fund,
          units: processedUnits.toFixed(4),
          amount: derivedAmount.toFixed(4),
        };

        // Recalculate block total amount
        const blockTotal = updatedSubFunds.reduce(
          (sum: number, f: any) => sum + parseFloat(f.amount || 0),
          0,
        );

        // Recalculate block total units
        const blockTotalUnits = updatedSubFunds.reduce(
          (sum: number, f: any) => sum + parseFloat(f.units || 0),
          0,
        );

        globalTotalAmount += blockTotal;
        globalTotalUnits += blockTotalUnits;

        return {
          ...block,
          subFunds: updatedSubFunds,
          total_amount: blockTotal.toFixed(4),
        };
      });

      // Sync top-level amount and units fields
      setAmount(globalTotalAmount.toFixed(4));
      setUnits(globalTotalUnits.toFixed(4));

      return updatedBlocks;
    });
  };

  const fetchParentFundAccounts = async (parentFundCode: string) => {
    if (!parentFundCode) return;
    try {
      const accounts = await fetchAccountsForFund(parentFundCode);
      const processedAccounts =
        accounts.length > 0
          ? accounts.map((account) => ({
              ...account,
              value: account.account_no,
              label: account.account_title,
            }))
          : accounts;
      const collectionAcc = findCollectionAccount(processedAccounts);
      setParentFundAccounts(processedAccounts);
      if (collectionAcc) {
        setParentFundAccount(collectionAcc);
        setFields((prev) => ({
          ...prev,
          account_number: collectionAcc.account_no || "",
          account_title: collectionAcc.account_title || "",
          bank: collectionAcc.bank_name || "",
          branch: collectionAcc.bank_city || "",
        }));
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to load parent fund accounts for ${parentFundCode}`,
      );
    }
  };

  const handleParentFundAccountChange = (selected: any) => {
    if (!selected) return;
    const account = parentFundAccounts.find(
      (acc) => acc.account_no === selected.value,
    );
    if (account) {
      setParentFundAccount(account);
      setFields((prev) => ({
        ...prev,
        account_number: account.account_no || "",
        account_title: account.account_title || "",
        bank: account.bank_name || "",
        branch: account.bank_city || "",
      }));
    }
  };

  return {
    viewMode,
    loadingViewData,
    fields,
    setFields,
    updateField,
    amcOptions,
    fundOptions,
    accountOptions,
    unitholderAccountOptions,
    modeOfPayments,
    instrumentTypes,
    loading,
    errorShow,
    handleAmcChange,
    handleFundChange,
    handleAccountChange,
    handleSaleDateChange,
    handleSubmit,
    getErrorClass,
    history,
    unitholderOptions,
    unitholderLoading,
    handleUnitholderChange,
    handleUnitholderInputChange,
    renderRange,
    removeSubFundsBlock,
    onAmountChange,
    handleCustomCheckboxChange,
    allFunds,
    setAllFunds,
    errors,
    setErrors,
    rangeTimer,
    setRangeTimer,
    autoInvest,
    setAutoInvest,
    typeVPS,
    setTypeVPS,
    unitholderType,
    setUnitholderType,
    amount,
    setAmount,
    fundLoading,
    setFundLoading,
    subFundsBlocks,
    setSubFundsBlocks,
    customStyles,
    fundAccountsMap,
    handleSubFundAccountChange,
    fetchAccountsForFund,
    calculateUnits,
    navLoading,
    setUnitholderOptions,
    currentSubFundsBlock,
    SetCurrentSubFundsBlock,
    handleChangeAllocation,
    handleSubFundChange,
    selectedSystematicVPSType,
    currAmount,
    setIndexedError,
    handleAmountChange,
    totalAmount,
    totalUnits,
    setUnits,
    units,
    offerPrice,
    id,
    showApproval,
    setShowApproval,
    authroizeDialogue,
    setAuthorizeDialogue,
    fetchNavForFund,
    calculateBlockTotal,
    decision,
    setDecision,
    rejectReason,
    setRejectReason,
    onSubmitStatus,
    compliant,
    setCompliant,
    compliantComment,
    setcompliantComment,
    noNav,
    updateNav,
    handleManualWithdrawalAmount,
    showVpsErrors,
    vpsErrorMessages,
    setShowVpsErrors,
    schemeOptions,
    initializeCoA,
    handleFromSchemeChange,
    handleToSchemeChange,
    handleToFundChange,
    addCoaFundRow,
    removeCoaFundRow,
    handleManualWithdrawalUnits,
    coaFunds,
    setCoaFunds,
    fromScheme,
    toScheme,
    handleFromUnitChange,
    handleCoaAmountChange,
    handleFromFundChange,
    handleFromPctChange,

    // Parent Fund Account Related States and handlers
    parentFundAccounts,
    setParentFundAccounts,

    parentFundAccount,
    setParentFundAccount,
    handleParentFundAccountChange,
    fetchParentFundAccounts,
  };
};
